import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { Button, Modal, NavLink } from "react-bootstrap";
import { BsTrash } from "react-icons/bs";
import { observer } from "mobx-react";
import dataStore from "../../Store/dataStore";
import * as itemService from "../../Services/Http/ItemService";
import * as salesOrderService from "../../Services/Http/SalesOrderService";
import { OrderItemPrice, SalesOrderDraft, SalesOrderDraftLine } from "../../Models/SalesOrder";
import { loadSalesOrderDraft, saveSalesOrderDraft, todayIso, SalesDocMode, salesDocText } from "../../Helpers/SalesOrderDraft";

//Order / Quote, step 2: scan items for the chosen customer, choose how many, see the price, then Post
//creates an SOR (order) or SQU (quote) in eSuite. Nothing is sent until then - it is kept on the device.
//Prices shown are the customer price from the site's pda_ProductList (as the HTC shop app prices, posted on the
//line like the shop does), or the customer's price band when that is not available - also posted on the line.

interface Props {
  //"quote" posts a sales quote instead of an order
  mode?: SalesDocMode;
}

const successSound = new Audio();
successSound.src = "/assets/sounds/success.mp3";
successSound.load();

const errorSound = new Audio();
errorSound.src = "/assets/sounds/error.mp3";
errorSound.load();

const money = (n: number): string => n.toLocaleString(navigator.language, { style: "currency", currency: "AUD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qtyText = (n: number): string => n.toLocaleString(navigator.language, { maximumFractionDigits: 3 });
const packFactor = (item: OrderItemPrice): number => (item.salesPackQty || 1) * (item.salesPackInnerQty || 1);
//"Ctn of 24" style description of what one sell pack is
const packText = (item: OrderItemPrice): string => {
  const factor = packFactor(item);
  return factor > 1 ? `${item.sellUnit || "pack"} of ${qtyText(factor)}` : item.sellUnit || "each";
};

const errorText = (error: any, fallback: string = "Something went wrong"): string => {
  if (typeof error === "string" && error) {
    return error;
  }
  return (error && typeof error.message === "string" && error.message) || fallback;
};

const SalesOrderPage = observer((props: Props) => {
  const mode: SalesDocMode = props.mode || "order";
  const text = salesDocText(mode);
  const navigate = useNavigate();
  const [draft, setDraftState] = useState<SalesOrderDraft | null>(() => loadSalesOrderDraft(mode));
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [busy, setBusy] = useState<boolean>(false);
  const [posting, setPosting] = useState<boolean>(false);
  const searchBoxInputRef = useRef<HTMLInputElement>(null);

  //the qty popup - for a newly scanned item, or a line being changed
  const [qtyItem, setQtyItem] = useState<OrderItemPrice | null>(null);
  const [qtyValue, setQtyValue] = useState<string>("1");
  const [qtyPrice, setQtyPrice] = useState<OrderItemPrice | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const setDraft = (next: SalesOrderDraft | null) => {
    setDraftState(next);
    saveSalesOrderDraft(mode, next);
  };

  useEffect(() => {
    if (!draft || !draft.customer) {
      navigate(text.customerRoute);
    }
    // only when the screen opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    searchBoxInputRef.current?.focus();
  }, []);

  //the price follows the qty typed, so quantity breaks show up before it is added
  useEffect(() => {
    if (!qtyItem || !draft) {
      return;
    }
    const qty = parseFloat(qtyValue);
    if (!(qty > 0)) {
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      salesOrderService
        .getOrderItemPriceAPI(qtyItem.stockCode, draft.customer, qty, dataStore.serverUrl || "", dataStore.dbKey || "")
        .then((p) => {
          if (!cancelled && p) {
            setQtyPrice(p);
          }
        })
        .catch(() => {
          //keep showing the last price
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // re-price when the qty or item changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qtyValue, qtyItem]);

  const fail = (message: string, autoClose: number | false = 2500) => {
    setSearchTerm("");
    errorSound.play();
    toast.error(message, { autoClose });
  };

  //------------- scanning -------------
  const onBarcodeScanned = useCallback(
    async (barcode: string): Promise<void> => {
      const code = (barcode || "").trim();
      if (!code || busy || posting || qtyItem || !draft) {
        return;
      }
      setBusy(true);
      try {
        //a product barcode, stock code or pallet label - pda_BarcodeScan works out which stock item it is
        const scan = await itemService.scanItemFirstAPI(code, dataStore.serverUrl || "", dataStore.dbKey || "");
        if (!scan || !scan.stockCode) {
          fail(scan?.status === 2 || !scan ? "Barcode not found" : "That is not a stock item");
          return;
        }
        const price = await salesOrderService.getOrderItemPriceAPI(scan.stockCode, draft.customer, 1, dataStore.serverUrl || "", dataStore.dbKey || "");
        setSearchTerm("");
        successSound.play();
        openQtyPopup(price, null);
      } catch (error: any) {
        const text = errorText(error);
        fail(/duplicated barcode/i.test(text) ? "That barcode matches more than one item - type the stock code instead" : text, false);
      } finally {
        setBusy(false);
      }
    },
    // the handlers read the latest draft
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busy, posting, qtyItem, draft],
  );

  const onKeyPressed = useCallback(
    (event: any) => {
      if (qtyItem || (event.target && (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement))) {
        return;
      }
      const keyPressed = event.key ? event.key : "";
      if (keyPressed.toLowerCase() === "tab" || keyPressed.toLowerCase() === "enter") {
        event.preventDefault();
        setSearchTerm((oldState) => {
          onBarcodeScanned(oldState);
          return oldState;
        });
      } else if (keyPressed.length === 1) {
        setSearchTerm((oldState) => oldState + keyPressed);
      }
    },
    [onBarcodeScanned, qtyItem],
  );

  useEffect(() => {
    window.addEventListener("keydown", onKeyPressed);
    return () => {
      window.removeEventListener("keydown", onKeyPressed);
    };
  }, [onKeyPressed]);

  //------------- qty popup -------------
  const openQtyPopup = (item: OrderItemPrice, index: number | null) => {
    setQtyItem(item);
    setQtyPrice(item);
    setEditingIndex(index);
    const existingQty = index !== null && draft ? draft.lines[index].qty : 1;
    setQtyValue(qtyText(existingQty).replace(/,/g, ""));
  };

  const closeQtyPopup = () => {
    setQtyItem(null);
    setQtyPrice(null);
    setEditingIndex(null);
    setTimeout(() => searchBoxInputRef.current?.focus(), 100);
  };

  const onConfirmQty = () => {
    if (!draft || !qtyItem) {
      return;
    }
    const qty = parseFloat(qtyValue);
    if (!(qty > 0)) {
      toast.error("Enter how many", { autoClose: 1500 });
      return;
    }
    const item = qtyPrice && qtyPrice.stockCode === qtyItem.stockCode ? qtyPrice : qtyItem;
    let lines: SalesOrderDraftLine[];
    if (editingIndex !== null) {
      lines = draft.lines.map((l, i) => (i === editingIndex ? { item, qty } : l));
    } else {
      //the same item scanned again adds to its line
      const same = draft.lines.findIndex((l) => l.item.stockCode === item.stockCode);
      lines = same >= 0 ? draft.lines.map((l, i) => (i === same ? { item, qty: l.qty + qty } : l)) : [...draft.lines, { item, qty }];
    }
    setDraft({ ...draft, lines });
    toast.success(`${qtyText(qty)} x ${item.stockCode}`, { autoClose: 800 });
    closeQtyPopup();
  };

  const stepQty = (delta: number) => {
    const current = parseFloat(qtyValue) || 0;
    const next = Math.max(1, Math.round((current + delta) * 1000) / 1000);
    setQtyValue(next.toString());
  };

  const onRemoveLine = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    if (!draft) {
      return;
    }
    const line = draft.lines[index];
    setDraft({ ...draft, lines: draft.lines.filter((_, i) => i !== index) });
    toast.info(`Removed ${line.item.stockCode}`, { autoClose: 1000 });
  };

  //------------- post / cancel -------------
  const orderTotal = (draft?.lines || []).reduce((sum, l) => sum + l.qty * (l.item.packPrice || 0), 0);

  const onPost = async () => {
    if (!draft || draft.lines.length === 0) {
      return;
    }
    setPosting(true);
    try {
      const resp = await salesOrderService.createSalesOrderAPI(
        {
          companyNo: draft.customer.companyNo,
          accountNo: draft.customer.accountNo,
          accountRef: draft.customer.accountRef,
          comment: draft.comment || "",
          dueDate: draft.dueDate || todayIso(),
          locationNo: dataStore.location,
          isQuote: mode === "quote",
          //the price shown goes on the line with its GST type; a line without one is left for eSuite to price
          lines: draft.lines.map((l) => ({
            stockCode: l.item.stockCode,
            qty: l.qty,
            unitPrice: l.item.postPrice ?? null,
            gstType: l.item.gstType,
            gstRate: l.item.gstRate,
            packQty: l.item.salesPackQty,
            packInnerQty: l.item.salesPackInnerQty,
          })),
        },
        dataStore.serverUrl || "",
        dataStore.dbKey || "",
      );
      successSound.play();
      toast.success(`${text.Noun} ${resp.transRefCode || ""} created`, { autoClose: 3000 });
      setDraft(null);
      navigate(text.customerRoute);
    } catch (error: any) {
      fail(errorText(error, `Posting the ${text.noun} failed`), false);
    } finally {
      setPosting(false);
    }
  };

  const onCancel = () => {
    if (draft && draft.lines.length > 0 && !window.confirm(`Discard this ${text.noun}? Nothing has been sent to eSuite.`)) {
      return;
    }
    setDraft(null);
    navigate(text.customerRoute);
  };

  if (!draft || !draft.customer) {
    return null;
  }

  const shownPrice = qtyPrice || qtyItem;
  const shownQty = parseFloat(qtyValue) || 0;
  return (
    <>
      <div className="row sticky-top-2">
        <div className="col-12 my-2">
          <div className="container-limited-width d-flex justify-content-between align-items-center">
            <div>
              <div style={{ fontSize: "1.2rem" }}>
                <b>{draft.customer.accountName || draft.customer.accountRef}</b>
              </div>
              <div className="text-muted small">{draft.customer.accountRef}</div>
            </div>
            <Button variant="outline-secondary" size="sm" onClick={() => navigate(text.customerRoute)} title="Change customer">
              Change
            </Button>
          </div>
        </div>
      </div>
      <div className="container-limited-width">
        <div className="row g-2">
          <div className="col-5">
            <label className="form-check-label small">Due date</label>
            <input type="date" className="form-control" value={draft.dueDate || todayIso()} onChange={(e) => setDraft({ ...draft, dueDate: e.target.value })} />
          </div>
          <div className="col-7">
            <label className="form-check-label small">Comment</label>
            <input type="text" className="form-control" maxLength={200} value={draft.comment} onChange={(e) => setDraft({ ...draft, comment: e.target.value })} />
          </div>
        </div>
        <div className="row sticky-top-3 search-bar">
          <div className="col-12 p-2">
            <input
              name="searchBox"
              type="text"
              className="form-control search-box"
              placeholder="Scan or type a stock code / barcode"
              autoCapitalize="none"
              autoComplete="off"
              value={searchTerm}
              disabled={busy || posting}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyUp={(e) => {
                if (e.key && e.key.toLowerCase() === "enter") {
                  e.preventDefault();
                  onBarcodeScanned(searchTerm);
                }
              }}
              ref={searchBoxInputRef}
            />
          </div>
        </div>
        {draft.lines.length === 0 && <div className="text-muted text-center my-4">No items yet - scan a stock code or barcode to add it.</div>}
        <ul className="list-group list-group-flush p-0">
          {draft.lines.map((l, index) => (
            <li key={`${l.item.stockCode}_${index}`} className="list-group-item d-flex align-items-start p-2" onClick={() => openQtyPopup(l.item, index)}>
              <div className="d-flex flex-column flex-grow-1">
                <div className="fw-bold">{l.item.stockDesc}</div>
                <div className="text-muted small">
                  {l.item.stockCode} · {packText(l.item)}
                </div>
                <div>
                  {qtyText(l.qty)} x {money(l.item.packPrice || 0)} = <b>{money(l.qty * (l.item.packPrice || 0))}</b>
                </div>
              </div>
              <Button variant="outline-danger" size="sm" className="ms-2" onClick={(e) => onRemoveLine(e, index)} disabled={posting} title="Remove this item">
                <BsTrash />
              </Button>
            </li>
          ))}
        </ul>
        {draft.lines.length > 0 && (
          <div className="d-flex justify-content-between fw-bold border-top pt-2 mt-2 mb-4">
            <span>
              Total ({draft.lines.length} item{draft.lines.length === 1 ? "" : "s"})
            </span>
            <span>{money(orderTotal)}</span>
          </div>
        )}
      </div>

      <Modal show={!!qtyItem} onHide={closeQtyPopup} centered>
        <Modal.Header closeButton={true}>
          <Modal.Title style={{ fontSize: "1.1rem" }}>{shownPrice?.stockDesc}</Modal.Title>
        </Modal.Header>
        {shownPrice && (
          <Modal.Body>
            <div className="text-muted small mb-2">
              {shownPrice.stockCode} · {packText(shownPrice)}
              {shownPrice.stockOnHandQty !== undefined && shownPrice.stockOnHandQty !== null && <> · {qtyText(shownPrice.stockOnHandQty)} on hand</>}
            </div>
            <label className="form-check-label">How many ({shownPrice.sellUnit || "packs"})</label>
            <div className="input-group mb-3">
              <Button variant="outline-secondary" onClick={() => stepQty(-1)}>
                -
              </Button>
              <input
                type="number"
                min="0"
                step="any"
                inputMode="decimal"
                className="form-control text-center"
                value={qtyValue}
                onChange={(e) => setQtyValue(e.target.value)}
                onKeyUp={(e) => {
                  if (e.key && e.key.toLowerCase() === "enter") {
                    onConfirmQty();
                  }
                }}
                autoFocus
              />
              <Button variant="outline-secondary" onClick={() => stepQty(1)}>
                +
              </Button>
            </div>
            <div className="d-flex justify-content-between">
              <span>Price</span>
              <span>
                {money(shownPrice.packPrice || 0)} / {shownPrice.sellUnit || "pack"}
                {shownPrice.priceBand ? <span className="text-muted small ms-1">({shownPrice.priceBand})</span> : null}
              </span>
            </div>
            <div className="d-flex justify-content-between fw-bold">
              <span>Line total</span>
              <span>{money(shownQty * (shownPrice.packPrice || 0))}</span>
            </div>
            {!shownPrice.incGST && (shownPrice.gstRate || 0) > 0 && (
              <div className="d-flex justify-content-between text-muted small">
                <span>Inc GST</span>
                <span>{money(shownQty * (shownPrice.packPrice || 0) * (1 + (shownPrice.gstRate || 0)))}</span>
              </div>
            )}
            <div className="text-muted small mt-1">
              {shownPrice.incGST ? "Includes GST" : "Excludes GST"} ·{" "}
              {shownPrice.priceSource === "ProductList"
                ? "customer price"
                : shownPrice.postPrice
                  ? `price band ${shownPrice.priceBand || ""}`.trim()
                  : "eSuite sets the final price"}
            </div>
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button variant="success" onClick={onConfirmQty}>
            {editingIndex !== null ? "Update" : "Add"}
          </Button>
          <Button variant="secondary" onClick={closeQtyPopup}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <footer className="footer footer-pinned mt-5 fixed-bottom">
        <div className="container-fluid d-flex justify-content-around bottom-nav pt-2">
          <NavLink className="d-flex flex-column" title="Cancel" onClick={onCancel} disabled={posting}>
            <i className="fa fa-times footer-icon" aria-hidden="true"></i>
            <span>Cancel</span>
          </NavLink>
          <NavLink className="d-flex flex-column" title={text.post} onClick={onPost} disabled={posting || busy || draft.lines.length === 0}>
            <i className="fa fa-paper-plane footer-icon" aria-hidden="true"></i>
            <span>{posting ? "Posting..." : text.post}</span>
          </NavLink>
        </div>
      </footer>
    </>
  );
});

export default SalesOrderPage;
